import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { SensorStatusVariant } from '../../lib/sensorStatus';

interface AccountDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Alias for onOpenChange(false) — both are accepted for ergonomic use */
  onClose?: () => void;
  /** Modal title (e.g. sensor name) */
  title: string;
  /** Optional subtitle (e.g. sensor type · site) */
  subtitle?: string;
  /** Optional badge label (e.g. status) */
  badgeLabel?: string;
  badgeVariant?: SensorStatusVariant;
  children: React.ReactNode;
}

/**
 * AccountDetailModal — the uniform large detail/form modal for all account tabs.
 *
 * Replaces AccountDetailPanel (DragResizeSidePanel) as the standard shell for
 * row-click detail views. Uses a plain shadcn Dialog with a controlled open
 * state (no resize, no ref wiring — reliable open/close).
 *
 * Size: ~92vw wide up to 5xl cap, 88vh tall — content is clearly visible on
 * all screen sizes.
 *
 * Structure:
 *   DialogContent (flex column, fixed height)
 *     ├─ sticky header: DialogTitle + optional subtitle + optional Badge
 *     └─ overflow-y-auto body slot (children)
 */
export function AccountDetailModal({
  open,
  onOpenChange,
  onClose,
  title,
  subtitle,
  badgeLabel,
  badgeVariant,
  children,
}: AccountDetailModalProps) {
  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) onClose?.();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'w-[92vw] sm:max-w-5xl max-h-[88vh]',
          'flex flex-col p-0 gap-0 overflow-hidden',
        )}
      >
        {/* Sticky header — visually prominent, includes the accessible DialogTitle */}
        <div className="sticky top-0 z-10 bg-background border-b px-6 py-4 shrink-0">
          <div className="flex items-start gap-3 pr-8">
            {/* pr-8 leaves room for the built-in DialogPrimitive.Close (×) button */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className="text-base font-semibold leading-snug truncate">
                  {title}
                </DialogTitle>
                {badgeLabel && (
                  <Badge
                    variant={badgeVariant as Parameters<typeof Badge>[0]['variant']}
                    className="text-xs shrink-0"
                  >
                    {badgeLabel}
                  </Badge>
                )}
              </div>
              {subtitle && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
